import React, { useState } from "react";
import HeaderPanel from "../layout/HeaderPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { mapTgkDuplicateElaborationRequest, normalizePath } from "../../redux/feature/document/document-slice";
import { useAppSelector } from "../../redux/hooks/hooks";
import { selectCpmUsername, selectTenantId } from "../../redux/feature/settings/configurations-slice";
import { elaborationStatusItemIsBlocking } from "../../redux/feature/object/remote-elaborations-slice";
import { DocumentData, EditDocument } from "./EditDocument";
import { fetchDocumentPartDetails } from "../../api/document/DocumentsPart";
import { duplicateDocumentRoot } from "../../api/document/Documents";
import { getDefaultBuiltInFormattingString } from "../../utils/helpers/ObjectFormattingHelper";
import { FormatTypeEnum } from "../../api/models/format-type-enum";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { API_CALL_NOTIFICATION, DUPLICATE_ELABORATION } from "../../utils/constants/notification";
import { getDocumentPartMetadataFromCustomXml } from "../../utils/helpers/CustomXmlHelper";
import { useFilteredElaborations } from "../generic/GenericElaborationSelector";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";
import { StandaloneModal } from "../layout/StandaloneModal";
import NotificationManager from "../../common/notification/NotificationManager";
import * as uuid from "uuid";
import RollForwardManager from "./RollForwardManager";
import { buildRollForwardPermissionModal, isPermissionCopyErrorText } from "./RollForwardPermission";

interface DuplicateProps {
  documentId: string;
  documentName: string;
  onCloseModal: () => void;
  channelIdToNotify: string;
}

const findBlockingElaboration = (item) => item.elaborationType === "DUPLICATE" && elaborationStatusItemIsBlocking(item);

export const DuplicateDocumentPanel: React.FunctionComponent<DuplicateProps> = (props) => {
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login
  const tenantId = useAppSelector(selectTenantId);
  const [permissionWarning, setPermissionWarning] = useState<{ data: DocumentData } | null>(null);

  const isPermissionCopyError = (err: any): boolean => {
    const errorData = err?.response?.data;
    if (err?.response?.status === 512 && Array.isArray(errorData) && errorData.length > 0) {
      return errorData.some((item: any) => isPermissionCopyErrorText(item?.error));
    }
    return false;
  };

  const submitDuplicate = async (data: DocumentData, copyRights?: boolean) => {
    const request = mapTgkDuplicateElaborationRequest({
      userId: username,
      documentId: props.documentId,
      sharepointPath: normalizePath(data.path),
      tenantId,
      siteId: null,
      correlationId: uuid.v4(),
      documentPartMetadata: data.parameters,
      documentName: data.name,
      description: data.description,
      advanced: data.advanced,
      language: data.language,
      channelIdToNotify: props.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      duplicateAttachments: data.duplicateAttachments,
      copySharePointUserRights: copyRights ?? data.copySharePointUserRights,
    });

    // Keep the payload available so the roll forward can be re-run without copying the user rights
    // if the asynchronous elaboration fails because the SharePoint user rights cannot be copied.
    RollForwardManager.storeDuplicatePayload(request);
    try {
      await duplicateDocumentRoot(request);
      props.onCloseModal();
    } catch (err) {
      RollForwardManager.removeDuplicatePayload(request.correlationId);
      throw err;
    }
  };

  const duplicateDocument = async (data: DocumentData) => {
    try {
      await submitDuplicate(data);
    } catch (err: any) {
      if (data.copySharePointUserRights && isPermissionCopyError(err)) {
        NotificationManager.remove(API_CALL_NOTIFICATION);
        setPermissionWarning({ data });
      }
    }
  };

  const blockingElaboration = useFilteredElaborations({
    findFunction: findBlockingElaboration,
  });

  return (
    <>
      {permissionWarning && (
        <StandaloneModal
          {...buildRollForwardPermissionModal({
            onContinueWithoutRights: () => {
              submitDuplicate(permissionWarning.data, false);
            },
            onCancel: () => props.onCloseModal(),
          })}
          onHide={() => setPermissionWarning(null)}
        />
      )}
      <ElaborationWrapper notificationKey={DUPLICATE_ELABORATION} elaborationItem={blockingElaboration}>
        <>
          <HeaderPanel data-testid={"header"} applyPadding>
            {formatMessage("cof.documentmanagement.outline.duplicate.tile.rollforwarddocument")}{" "}
            {props.documentName ? " - " + props.documentName : null}
          </HeaderPanel>
          <EditDocument
            onLastBackButton={() => props.onCloseModal()}
            isEdit={false}
            editDocumentOperationType={"DUPLICATE"}
            onSave={(data) => duplicateDocument(data)}
            getDocumentPartDetails={async () => {
              const documentPartMetadata = await getDocumentPartMetadataFromCustomXml();
              const documentPart = await fetchDocumentPartDetails(props.documentId);

              const advanced =
                documentPart.root || documentPart.multiLinked
                  ? documentPartMetadata
                  : {
                      formatMetadata: {
                        numberFormat: {
                          format: getDefaultBuiltInFormattingString(FormatTypeEnum.NUMBER),
                        },
                        numberType: "Default",
                        percentageFormat: {
                          format: getDefaultBuiltInFormattingString(FormatTypeEnum.PERCENTAGE),
                        },
                        percentageType: "Default",
                        dateFormat: {
                          format: getDefaultBuiltInFormattingString(FormatTypeEnum.DATE),
                        },
                        dateType: "Default",
                        locale: documentPartMetadata?.formatMetadata?.locale,
                      },
                      formatImage: {
                        imageExtension: "png",
                        imageResolution: 150,
                      },
                      templateInfo: documentPartMetadata?.templateInfo,
                    };
              return {
                sourceDocumentName: documentPart.name,
                documentData: {
                  description: documentPart.description,
                  name: "",
                  root: true,
                  multilinked: false,
                  hasChildren: documentPart.hasChildren,
                  path: "",
                  documentId: documentPart.id,
                  siteId: documentPart.siteId,
                  driveItemId: documentPart.driveItemId,
                  advanced,
                  language: documentPartMetadata.language,
                  parameters:
                    documentPartMetadata.parametersMetadata?.map((p) => ({
                      ...p,
                      dimensions: p.dimensions.map((d) => ({ ...d, inherited: false })),
                    })) ?? [],
                  inheritableParameters: undefined,
                },
              };
            }}
          />
        </>
      </ElaborationWrapper>
    </>
  );
};
